import { useState } from 'react';

// ── Badge ──────────────────────────────────────────────────────────────────
const BADGE_VARIANTS = {
  neutral: { bg: 'var(--surface-2)', color: 'var(--text-2)',     border: 'var(--border)' },
  ok:      { bg: 'var(--ok-bg)',     color: 'var(--ok)',         border: 'rgba(61,199,91,0.2)' },
  info:    { bg: 'var(--info-bg)',   color: 'var(--info)',       border: 'rgba(59,130,246,0.2)' },
  warn:    { bg: 'var(--warn-bg)',   color: 'var(--warn)',       border: 'rgba(245,158,11,0.22)' },
  danger:  { bg: 'var(--danger-bg)', color: 'var(--danger)',     border: 'rgba(239,68,68,0.22)' },
  navy:    { bg: 'var(--navy-bg)',   color: 'var(--navy)',       border: 'var(--border)' },
  accent:  { bg: 'var(--accent-bg)', color: 'var(--accent-600)', border: 'rgba(61,199,91,0.22)' },
};
const BADGE_SIZES = {
  xs: { fs: 10, pad: '1px 6px' },
  sm: { fs: 11, pad: '2px 8px' },
  md: { fs: 12, pad: '3px 10px' },
};

export function Badge({ variant = 'neutral', size = 'sm', children, dot, style }) {
  // Fall back to neutral if an unknown variant is passed in — shouldn't crash the page
  const v = BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral;
  const s = BADGE_SIZES[size]       || BADGE_SIZES.sm;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: s.pad, fontSize: s.fs, fontWeight: 600, letterSpacing: '-0.005em',
      color: v.color, background: v.bg, border: `1px solid ${v.border}`,
      borderRadius: 999, whiteSpace: 'nowrap', ...style,
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: v.color }}/>}
      {children}
    </span>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────
export function Card({ children, style, hover = false, onClick }) {
  const [h, setH] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setH(true)}
      onMouseLeave={() => hover && setH(false)}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        boxShadow: h ? 'var(--shadow-md)' : 'var(--shadow-xs)',
        transition: 'box-shadow 0.15s, transform 0.12s, border-color 0.12s',
        cursor: onClick ? 'pointer' : 'default',
        transform: h && onClick ? 'translateY(-1px)' : 'none',
        borderColor: h && onClick ? 'var(--border-strong)' : 'var(--border)',
        ...style,
      }}>
      {children}
    </div>
  );
}

// ── Input ──────────────────────────────────────────────────────────────────
export function Input({ icon: I, type = 'text', style, ...rest }) {
  // Spacing from call sites (style={{ marginBottom: 14 }}) must land on the
  // WRAPPER. If it lands on the inner <input>, the margin grows the relative
  // wrapper and the absolute icon overlay (top:0/bottom:0) centers on
  // field+margin — the icon sits visibly low. Same family as the line-box
  // note below. Everything non-margin still styles the <input> itself.
  const {
    margin, marginTop, marginRight, marginBottom, marginLeft,
    ...inputStyle
  } = style || {};
  return (
    <div style={{
      position: 'relative', width: '100%',
      margin, marginTop, marginRight, marginBottom, marginLeft,
    }}>
      {I && (
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: 36,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-muted)', pointerEvents: 'none',
        }}>
          <I size={15}/>
        </div>
      )}
      <input
        type={type}
        className="mi-focusable mi-input"
        {...rest}
        style={{
          boxSizing: 'border-box',
          // display:block is load-bearing. A default inline-block <input> turns the
          // position:relative wrapper into a line box ~16px taller than the field
          // (baseline/descender slack below the input). The absolute icon overlay
          // (top:0/bottom:0, centered) then centers in that taller box and sits low.
          // block removes the line box so the icon centers on the field itself.
          display: 'block',
          width: '100%', height: 40, padding: I ? '0 14px 0 36px' : '0 14px',
          fontSize: 13.5, fontFamily: 'inherit', color: 'var(--text)',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)', transition: 'border-color 0.12s, box-shadow 0.12s',
          ...inputStyle,
        }}
      />
    </div>
  );
}

// ── Avatar ─────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#0a0f2e','#3dc75b','#f59e0b','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#ef4444'];

export function Avatar({ name = '?', color, size = 36, src }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg = color || AVATAR_COLORS[hash % AVATAR_COLORS.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.3,
      background: src ? '#ddd' : bg, color: '#fff', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', fontWeight: 700,
      fontSize: size * 0.38, letterSpacing: '-0.02em', flexShrink: 0,
      backgroundImage: src ? `url(${src})` : 'none', backgroundSize: 'cover',
    }}>
      {!src && initials}
    </div>
  );
}

// ── Divider ────────────────────────────────────────────────────────────────
export function Divider({ style }) {
  return <div style={{ height: 1, background: 'var(--border)', ...style }}/>;
}

// ── Tooltip ────────────────────────────────────────────────────────────────
export function Tooltip({ label, children }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          padding: '4px 8px', background: 'var(--navy)', color: '#fff',
          fontSize: 11, fontWeight: 500, borderRadius: 6, whiteSpace: 'nowrap',
          zIndex: 10, pointerEvents: 'none',
        }}>
          {label}
        </span>
      )}
    </span>
  );
}

// ── Sparkline ──────────────────────────────────────────────────────────────
export function Sparkline({ data, color = 'var(--accent)', height = 28, width = 100 }) {
  const max = Math.max(...data, 1);
  const bw = width / data.length;
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {data.map((v, i) => {
        const h = (v / max) * height;
        return (
          <rect
            key={i} x={i * bw + 1} y={height - h} width={bw - 2} height={h}
            rx={1.5} fill={color} opacity={0.5 + (i / data.length) * 0.5}
          />
        );
      })}
    </svg>
  );
}

// ── Ring ───────────────────────────────────────────────────────────────────
export function Ring({
  value = 0, total = 1, size = 64, stroke = 6,
  color = 'var(--accent)', track = 'var(--border)',
  label, sublabel,
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, value / total));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} stroke={track} strokeWidth={stroke} fill="none"/>
        <circle
          cx={size/2} cy={size/2} r={r} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s cubic-bezier(0.2, 0.8, 0.3, 1)' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      }}>
        <div style={{
          fontSize: size * 0.28, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1,
        }}>
          {label}
        </div>
        {sublabel && (
          <div style={{
            fontSize: size * 0.13, color: 'var(--text-3)', marginTop: 2, fontWeight: 500,
          }}>
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Spinner ────────────────────────────────────────────────────────────────
export function Spinner({ size = 16, color = 'currentColor' }) {
  return (
    <span
      className="mi-spin"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        border: `2px solid ${color}`,
        borderTopColor: 'transparent',
        borderRadius: '50%',
      }}
    />
  );
}

// ── ComingSoon ribbon ──────────────────────────────────────────────────────
export function ComingSoon({ style }) {
  return (
    <span className="coming-soon-badge" style={style}>
      Coming soon
    </span>
  );
}
